import { classMap, Component, html, LitElement, property, state, unsafeCSS } from '@rxdi/lit-html';
import * as CodeEditorComponentCSS from 'bundle-text:./code-editor.component.css';
export type SupportedLanguages = 'typescript' | 'javascript' | 'python';

interface Token {
  type: string;
  value: string;
}

type MutableValidity = {
  -readonly [K in keyof ValidityState]: ValidityState[K];
};
type Writable<T> = {
  -readonly [P in keyof T]: T[P];
};

/**
 * @customElement code-editor
 */
@Component({
  selector: 'code-editor',
  styles: [unsafeCSS(CodeEditorComponentCSS)],
  template(this: CodeEditorComponent) {
    return html`
      <div class="editor-wrapper" part="editor-wrapper">
        <textarea
          part="textarea"
          class=${classMap({ outline: !this.readonly })}
          spellcheck="false"
          ?disabled="${this.disabled}"
          ?readonly="${this.readonly}"
          ?required="${this.required}"
          .tabIndex="${this.disabled ? -1 : 0}"
          aria-label="${this.ariaLabel || 'Code Editor'}"
          aria-invalid="${!this.isValid}"
          aria-required="${this.required}"
          @input="${this.handleInput}"
          @change="${this.handleChange}"
          @blur="${this.handleBlur}"
          @focus="${this.handleFocus}"
          @scroll="${this.syncScroll}"
          @keydown="${this.handleTab}"
        ></textarea>
        <pre class="highlight-layer" aria-hidden="true" .innerHTML="${this.getHighlightedHTML()}"></pre>
      </div>
      <div class="status-bar">
        <span class="${this.isValid ? '' : 'error'}">
          ${this.isValid ? '✓ Valid Code' : `✗ ${this.customValidationMessage || 'Invalid Code'}`}
        </span>
        <span
          >${this.language.toUpperCase()} • ${this.value.split('\n').length} lines • ${this.value.length} chars</span
        >
      </div>
    `;
  },
})
export class CodeEditorComponent extends LitElement {
  static formAssociated = true;

  private _internals: ElementInternals;
  private _value = '';
  private _customValidationMessage = '';
  private _isUserInput = false;

  @property({ type: String, reflect: true }) theme: 'black' | 'white' = 'black';
  @property({ type: String, reflect: true }) language: SupportedLanguages = 'typescript';
  @property({ type: String, reflect: true }) name = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: String }) ariaLabel = '';
  @property({ type: Number }) minLength = 0;
  @property({ type: Number }) maxLength = Infinity;
  @property({ type: Boolean, reflect: true }) autoScroll = false;

  @state() private isValid = true;

  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  get value() {
    return this._value;
  }

  set value(val: string) {
    if (val === this._value) return;
    this._value = val === null || val === undefined ? '' : String(val);
    if (!this._isUserInput) {
      const textarea = this.shadowRoot?.querySelector('textarea');
      if (textarea) textarea.value = this._value;
      if (this.autoScroll) {
        this.scrollToBottom(textarea);
      }
      this.requestUpdate();
    }
    this.validateAndUpdateForm();
  }

  private scrollToBottom(textarea: HTMLTextAreaElement) {
    setTimeout(() => {
      textarea.scrollTop = textarea.scrollHeight;
      this.syncScroll();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'textbox');
  }

  updated(changedProperties: Map<string, string>) {
    super.updated(changedProperties);
    this._isUserInput = false;
  }

  OnUpdateFirst() {
    this.validateAndUpdateForm();
    const textarea = this.shadowRoot?.querySelector('textarea');
    if (textarea) textarea.value = this._value;
  }

  private handleInput(e: Event) {
    if (this.readonly) return;
    const target = e.target as HTMLTextAreaElement;
    this._isUserInput = true;
    this._value = target.value;
    this.validateAndUpdateForm();
    this.syncScroll();
    this.requestUpdate();

    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    if (this.isValid) this.sendChangeEvent();
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: this.isValid,
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleChange() {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  private handleBlur() {
    this.dispatchEvent(new FocusEvent('blur', { bubbles: true, composed: true }));
  }

  private handleFocus() {
    this.dispatchEvent(new FocusEvent('focus', { bubbles: true, composed: true }));
  }

  private sendChangeEvent() {
    this.dispatchEvent(
      new CustomEvent('onValueChange', {
        detail: this._value,
        bubbles: true,
        composed: true,
      })
    );
  }

  private syncScroll() {
    const textarea = this.shadowRoot?.querySelector('textarea');
    const highlight = this.shadowRoot?.querySelector('.highlight-layer');
    if (textarea && highlight) {
      highlight.scrollTop = textarea.scrollTop;
      highlight.scrollLeft = textarea.scrollLeft;
    }
  }

  private handleTab(e: KeyboardEvent) {
    if (this.readonly) return;
    if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();

      // Use execCommand to preserve undo history
      document.execCommand('insertText', false, '  ');
    }
  }

  /**
   * Safe Highlighting: Tokenize -> Escape -> Wrap
   * This prevents replacing keywords inside HTML tags.
   */
  private getHighlightedHTML(): string {
    if (!this._value) return '';
    return this._value
      .split('\n')
      .map((line) => {
        const tokens = this.tokenizeLine(line, this.language);
        return tokens.map((t) => `<span class="${t.type}">${this.escapeHtml(t.value)}</span>`).join('');
      })
      .join('\n');
  }

  private escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private tokenizeLine(line: string, lang: SupportedLanguages): Token[] {
    const tokens: Token[] = [];
    let rest = line;

    // Get granular syntax maps
    const { storage, control, module, modifier, builtins } = this.getKeywordSets(lang);

    while (rest.length > 0) {
      let matched = false;

      // 1. Comments
      const isPython = lang === 'python';
      const commentStart = isPython ? '#' : '//';
      if (rest.startsWith(commentStart)) {
        tokens.push({ type: 'comment', value: rest });
        rest = '';
        matched = true;
        continue;
      }

      // 2. Strings (Double Quote)
      if (rest.startsWith('"')) {
        let i = 1;
        while (i < rest.length) {
          if (rest[i] === '"' && rest[i - 1] !== '\\') break;
          i++;
        }
        if (i < rest.length) {
          const str = rest.substring(0, i + 1);
          tokens.push({ type: 'string', value: str });
          rest = rest.substring(i + 1);
          matched = true;
          continue;
        }
      }

      // 3. Strings (Single Quote)
      if (rest.startsWith("'")) {
        let i = 1;
        while (i < rest.length) {
          if (rest[i] === "'" && rest[i - 1] !== '\\') break;
          i++;
        }
        if (i < rest.length) {
          const str = rest.substring(0, i + 1);
          tokens.push({ type: 'string', value: str });
          rest = rest.substring(i + 1);
          matched = true;
          continue;
        }
      }

      // 4. Template Strings (JS/TS)
      if (!isPython && rest.startsWith('`')) {
        let i = 1;
        while (i < rest.length) {
          if (rest[i] === '`' && rest[i - 1] !== '\\') break;
          i++;
        }
        if (i < rest.length) {
          const str = rest.substring(0, i + 1);
          tokens.push({ type: 'string', value: str });
          rest = rest.substring(i + 1);
          matched = true;
          continue;
        }
      }

      // 5. Numbers
      const numberMatch = rest.match(/^\d+(?:\.\d+)?/);
      if (numberMatch) {
        tokens.push({ type: 'number', value: numberMatch[0] });
        rest = rest.substring(numberMatch[0].length);
        matched = true;
        continue;
      }

      // 6. Words (Keywords, Identifiers, Functions)
      const wordMatch = rest.match(/^[a-zA-Z_$][a-zA-Z0-9_$]*/);
      if (wordMatch) {
        const word = wordMatch[0];

        if (storage.has(word)) {
          tokens.push({ type: 'keyword-storage', value: word });
        } else if (control.has(word)) {
          tokens.push({ type: 'keyword-control', value: word });
        } else if (module.has(word)) {
          tokens.push({ type: 'keyword-module', value: word });
        } else if (modifier.has(word)) {
          tokens.push({ type: 'keyword-modifier', value: word });
        } else if (builtins.has(word)) {
          tokens.push({ type: 'variable', value: word }); // or built-in
        } else {
          // Heuristics
          // Is it followed by '(' ? -> Function // simplistic lookahead check from ORIGINAL STRING not supported easily here
          // But we can check rest of line? No, rest is modified.
          // Let's rely on convention.

          if (lang === 'typescript' && /^[A-Z]/.test(word)) {
            tokens.push({ type: 'type', value: word });
          } else {
            // Check next char in raw string?
            // Since we slice rest... let's check rest after word
            const after = rest.substring(word.length).trimLeft();
            if (after.startsWith('(')) {
              tokens.push({ type: 'function', value: word });
            } else {
              tokens.push({ type: 'variable', value: word });
            }
          }
        }
        rest = rest.substring(word.length);
        matched = true;
        continue;
      }

      // 7. Punctuation / Whitespace match one char
      if (!matched) {
        tokens.push({ type: '', value: rest[0] });
        rest = rest.substring(1);
      }
    }

    return tokens;
  }

  private getKeywordSets(lang: SupportedLanguages) {
    if (lang === 'python') {
      return {
        storage: new Set(['def', 'class', 'lambda', 'del', 'global', 'nonlocal']),
        control: new Set([
          'if',
          'elif',
          'else',
          'for',
          'while',
          'break',
          'continue',
          'pass',
          'return',
          'yield',
          'try',
          'except',
          'finally',
          'raise',
          'with',
          'assert',
        ]),
        module: new Set(['import', 'from', 'as']),
        modifier: new Set(['async', 'await']), // Python 3.5+
        builtins: new Set(['True', 'False', 'None', 'and', 'or', 'not', 'is', 'in']),
      };
    }

    // TS / JS
    return {
      storage: new Set([
        'const',
        'let',
        'var',
        'function',
        'class',
        'interface',
        'type',
        'enum',
        'namespace',
        'declare',
        'module',
        'constructor',
      ]),
      control: new Set([
        'if',
        'else',
        'for',
        'while',
        'do',
        'switch',
        'case',
        'default',
        'break',
        'continue',
        'try',
        'catch',
        'finally',
        'throw',
        'return',
        'new',
      ]),
      module: new Set(['import', 'export', 'from', 'as', 'require', 'package']),
      modifier: new Set([
        'async',
        'await',
        'static',
        'public',
        'private',
        'protected',
        'readonly',
        'extends',
        'implements',
        'abstract',
      ]),
      builtins: new Set([
        'this',
        'super',
        'void',
        'any',
        'boolean',
        'number',
        'string',
        'null',
        'undefined',
        'true',
        'false',
        'instanceof',
        'typeof',
        'keyof',
        'is',
        'in',
      ]),
    };
  }

  // Form API
  private validateAndUpdateForm() {
    const validityState: Partial<MutableValidity> = {};
    let validationMessage = '';

    if (this.required && (!this._value || this._value.trim() === '')) {
      validityState.valueMissing = true;
      validationMessage = 'This field is required';
    }

    this.isValid = Object.keys(validityState).length === 0;

    if (this.isValid && this._customValidationMessage) {
      this.isValid = false;
      validityState.customError = true;
      validationMessage = this._customValidationMessage;
    }

    if (this._internals) {
      this._internals.setValidity(
        validityState,
        validationMessage,
        this.shadowRoot?.querySelector('textarea') || undefined
      );
      this._internals.setFormValue(this._value);
    }
  }

  formResetCallback() {
    this.value = '';
    this._customValidationMessage = '';
    this.requestUpdate();
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state: string) {
    this.value = state;
  }

  setCustomValidity(message: string) {
    this._customValidationMessage = message;
    this.validateAndUpdateForm();
  }

  get customValidationMessage() {
    return this._customValidationMessage;
  }

  set customValidationMessage(data) {
    this._customValidationMessage = data;
  }

  get form() {
    return this._internals.form;
  }

  get type() {
    return 'code-editor';
  }

  get validity() {
    return this._internals.validity;
  }

  get validationMessage() {
    return this._internals.validationMessage;
  }

  get willValidate() {
    return this._internals.willValidate;
  }

  checkValidity() {
    return this._internals.checkValidity();
  }

  reportValidity() {
    return this._internals.reportValidity();
  }
}
